/**
 * Assessment Player Component
 * Main container for taking an assessment
 */

import React, { useState, useEffect } from 'react';
import { Response } from '../../types/models';
import { attemptsApi, GetAttemptByIdResponse } from '../api/attempts.js';
import { QuestionRenderer } from './QuestionRenderer';
import { MediaImage } from './MediaImage';

interface AssessmentPlayerProps {
  attemptId: string;
  onComplete: (results: any) => void;
}

export const AssessmentPlayer: React.FC<AssessmentPlayerProps> = ({
  attemptId,
  onComplete,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attemptData, setAttemptData] = useState<GetAttemptByIdResponse | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState<Map<string, { selectedOptionIds?: string[]; textResponse?: string }>>(new Map());
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Load attempt data
  useEffect(() => {
    loadAttempt();
  }, [attemptId]);

  // Timer effect
  useEffect(() => {
    if (!attemptData?.assessment.time_limit_seconds) {
      return;
    }

    const timeLimit = attemptData.assessment.time_limit_seconds;
    const elapsed = attemptData.time_elapsed_seconds;
    const remaining = timeLimit - elapsed;

    if (remaining <= 0) {
      handleComplete();
      return;
    }

    setTimeRemaining(remaining);

    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(interval);
          handleComplete();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [attemptData]);

  const loadAttempt = async () => {
    try {
      setLoading(true);
      const data = await attemptsApi.getById(attemptId);
      setAttemptData(data);

      // Resume at the saved question index
      const savedIndex = data.current_question_index ?? 0;
      setCurrentQuestionIndex(savedIndex);

      // Load existing responses
      const responseMap = new Map();
      data.responses.forEach((response: Response) => {
        responseMap.set(response.question_id, {
          selectedOptionIds: response.selected_option_ids,
          textResponse: response.text_response || undefined,
        });
      });
      setResponses(responseMap);
    } catch (err: any) {
      setError(err.message || 'Failed to load attempt');
    } finally {
      setLoading(false);
    }
  };

  const handleResponseChange = (questionId: string, response: { selectedOptionIds?: string[]; textResponse?: string }) => {
    const newResponses = new Map(responses);
    newResponses.set(questionId, response);
    setResponses(newResponses);

    // Auto-save response
    saveResponse(questionId, response);
  };

  const saveResponse = async (questionId: string, response: { selectedOptionIds?: string[]; textResponse?: string }) => {
    try {
      await attemptsApi.respond(attemptId, {
        question_id: questionId,
        selected_option_ids: response.selectedOptionIds,
        text_response: response.textResponse,
      });
    } catch (err) {
      console.error('Failed to save response:', err);
    }
  };

  const handleNext = () => {
    if (!questionAnswered) {
      return; // Don't allow navigation if question isn't answered
    }
    if (attemptData && currentQuestionIndex < attemptData.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleComplete = async () => {
    if (submitting) return;

    try {
      setSubmitting(true);
      const results = await attemptsApi.complete(attemptId);
      onComplete(results);
    } catch (err: any) {
      setError(err.message || 'Failed to complete attempt');
      setSubmitting(false);
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return <div className="loading">Loading assessment...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  if (!attemptData) {
    return <div className="error">Attempt not found</div>;
  }

  if (!attemptData.questions || attemptData.questions.length === 0) {
    return <div className="error">No questions found for this assessment</div>;
  }

  const currentQuestion = attemptData.questions[currentQuestionIndex];
  if (!currentQuestion) {
    return <div className="error">Question not found</div>;
  }

  const currentResponse = responses.get(currentQuestion.id) || {};
  const isLastQuestion = currentQuestionIndex === attemptData.questions.length - 1;
  const isFirstQuestion = currentQuestionIndex === 0;

  // Check if current question has been answered
  const isQuestionAnswered = () => {
    if (currentQuestion.question_type === 'short_answer') {
      return currentResponse.textResponse && currentResponse.textResponse.trim().length > 0;
    } else {
      // For multiple choice, multi-select, true/false, matching
      return currentResponse.selectedOptionIds && currentResponse.selectedOptionIds.length > 0;
    }
  };

  const questionAnswered = isQuestionAnswered();

  // Get current category
  const currentCategory = currentQuestion.category || 'Uncategorized';
  
  // Find all questions in the same category
  const categoryQuestions = attemptData.questions.filter(
    (q) => (q.category || 'Uncategorized') === currentCategory
  );
  const categoryQuestionIndex = categoryQuestions.findIndex((q) => q.id === currentQuestion.id);
  const categoryQuestionNumber = categoryQuestionIndex + 1;
  const totalInCategory = categoryQuestions.length;

  return (
    <>
      {attemptData.assessment.image_url && (
        <div className="assessment-image-header">
          <MediaImage
            src={attemptData.assessment.image_url}
            alt={attemptData.assessment.title}
            width={attemptData.assessment.image_width}
            height={attemptData.assessment.image_height}
          />
        </div>
      )}
      <div className="assessment-player">
        <div className="assessment-header">
        <h2>{attemptData.assessment.title}</h2>
        <div className="assessment-info">
          <span className="question-counter">
            Question {currentQuestionIndex + 1} of {attemptData.questions.length}
          </span>
          {timeRemaining !== null && (
            <span className="timer">Time remaining: {formatTime(timeRemaining)}</span>
          )}
        </div>
      </div>

      {currentCategory !== 'Uncategorized' && (
        <div className="category-header">
          <h3>{currentCategory}</h3>
          <span className="category-counter">
            Question {categoryQuestionNumber} of {totalInCategory} in this category
          </span>
        </div>
      )}

      <div className="question-container">
        <QuestionRenderer
          question={currentQuestion}
          selectedOptionIds={currentResponse.selectedOptionIds}
          textResponse={currentResponse.textResponse}
          onChange={(response) => handleResponseChange(currentQuestion.id, response)}
        />
      </div>

      <div className="navigation">
        <button
          onClick={handlePrevious}
          disabled={isFirstQuestion}
          className="btn btn-secondary"
        >
          Previous
        </button>
        {isLastQuestion ? (
          <button
            onClick={handleComplete}
            disabled={submitting || !questionAnswered}
            className="btn btn-primary"
          >
            {submitting ? 'Submitting...' : 'Submit Assessment'}
          </button>
        ) : (
          <button
            onClick={handleNext}
            disabled={!questionAnswered}
            className="btn btn-primary"
          >
            Next
          </button>
        )}
      </div>
      </div>
    </>
  );
};
